<template>
  <div class="row account-form-page">
    <div class="col-12 account-form-column order-xl-1">
      <card
        class="account-form-card"
        header-classes="account-form-card-header"
        card-body-classes="account-form-card-body"
        no-footer-line
      >
        <div slot="header" class="account-form-header">
          <div>
            <p class="account-form-eyebrow">Administration</p>
            <h4 class="card-title mb-1">
              {{ isEditMode ? `Compte : ${form.name || 'Chargement…'}` : 'Créer un compte' }}
            </h4>
            <p class="account-form-subtitle">
              {{ isEditMode ? 'Identité, accès et préférences du compte' : 'Créer un accès par invitation ou avec un mot de passe' }}
            </p>
          </div>
          <n-button type="default" size="sm" class="account-button account-button--secondary account-back-button" :disabled="isBusy" @click.native="backToList">
            <span><i class="fas fa-arrow-left mr-2" aria-hidden="true"></i>Retour à la liste</span>
          </n-button>
        </div>

        <div v-if="isLoading" class="account-form account-form--loading" aria-live="polite">
          <span class="sr-only">Chargement du formulaire…</span>
          <div class="account-form-layout" aria-hidden="true">
            <section class="account-form-section account-form-section--identity">
              <div class="account-form-section-heading account-skeleton-heading">
                <span class="account-form-section-icon account-skeleton"></span>
                <div class="account-form-section-heading-copy">
                  <span class="account-skeleton account-skeleton--heading"></span>
                </div>
              </div>
              <div class="account-form-grid account-form-grid--identity">
                <div v-for="field in 4" :key="`identity-${field}`" class="account-field account-skeleton-field" :class="{ 'account-skeleton-field--status': field === 4 }">
                  <span class="account-skeleton account-skeleton--label"></span>
                  <span class="account-skeleton account-skeleton--control"></span>
                </div>
              </div>
            </section>

            <section class="account-form-section account-form-section--appearance">
              <div class="account-form-section-heading account-skeleton-heading">
                <span class="account-form-section-icon account-skeleton"></span>
                <div class="account-form-section-heading-copy">
                  <span class="account-skeleton account-skeleton--heading account-skeleton--heading-long"></span>
                </div>
              </div>
              <div class="account-spinner-layout">
                <div class="account-field account-skeleton-field">
                  <span class="account-skeleton account-skeleton--label"></span>
                  <span class="account-skeleton account-skeleton--control"></span>
                </div>
                <div class="account-spinner-preview account-skeleton-preview">
                  <div class="account-spinner-preview-copy">
                    <span class="account-skeleton account-skeleton--preview-label"></span>
                    <span class="account-skeleton account-skeleton--preview-name"></span>
                  </div>
                  <span class="account-spinner-preview-visual account-skeleton"></span>
                </div>
              </div>
            </section>

            <section class="account-form-section account-form-section--access">
              <div class="account-form-section-header">
                <div class="account-form-section-heading account-skeleton-heading">
                  <span class="account-form-section-icon account-skeleton"></span>
                  <div class="account-form-section-heading-copy">
                    <span class="account-skeleton account-skeleton--heading"></span>
                  </div>
                </div>
                <span v-if="isEditMode" class="account-skeleton account-skeleton--section-action"></span>
              </div>

              <div v-if="!isEditMode" class="account-creation-options">
                <div v-for="option in 2" :key="`creation-${option}`" class="account-creation-option account-creation-option--skeleton">
                  <span class="account-creation-option-icon account-skeleton"></span>
                  <span class="account-creation-option-copy">
                    <span class="account-skeleton account-skeleton--option-title"></span>
                    <span class="account-skeleton account-skeleton--option-copy"></span>
                  </span>
                  <span class="account-creation-option-check account-skeleton"></span>
                </div>
              </div>

              <template v-else>
                <div class="account-form-grid account-password-grid">
                  <div v-for="field in 2" :key="`password-${field}`" class="account-field account-skeleton-field">
                    <span class="account-skeleton account-skeleton--label"></span>
                    <span class="account-skeleton account-skeleton--control"></span>
                  </div>
                  <div class="account-password-meter account-password-meter--skeleton">
                    <div class="account-password-meter-heading">
                      <span class="account-skeleton account-skeleton--meter-label"></span>
                      <span class="account-skeleton account-skeleton--meter-value"></span>
                    </div>
                    <div class="account-password-meter-track">
                      <span v-for="segment in 5" :key="`meter-${segment}`" class="account-skeleton"></span>
                    </div>
                    <span class="account-skeleton account-skeleton--meter-help"></span>
                  </div>
                </div>
              </template>
            </section>

            <section v-if="isEditMode" class="account-form-section account-form-section--history">
              <div class="account-form-section-heading account-skeleton-heading">
                <span class="account-form-section-icon account-skeleton"></span>
                <div class="account-form-section-heading-copy">
                  <span class="account-skeleton account-skeleton--heading"></span>
                </div>
              </div>
              <p class="account-history-empty">
                <span class="account-skeleton account-skeleton--history-empty"></span>
              </p>
            </section>
          </div>

          <div class="account-form-footer account-form-footer--skeleton">
            <div class="account-form-footer-actions">
              <span class="account-skeleton account-skeleton--footer-button account-skeleton--footer-button-secondary"></span>
              <span class="account-skeleton account-skeleton--footer-button account-skeleton--footer-button-primary"></span>
            </div>
          </div>
        </div>

        <div v-else-if="loadError" class="account-form-state" role="alert">
          <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
          <h5>Impossible de charger le compte</h5>
          <p>{{ loadError }}</p>
          <n-button type="primary" size="sm" class="account-button account-button--primary" @click.native="initialiseForm">
            <span><i class="fas fa-redo mr-2" aria-hidden="true"></i>Réessayer</span>
          </n-button>
        </div>

        <form v-else class="account-form" novalidate @submit.prevent="submitForm">
          <div v-if="isInvitationPending" class="account-form-notice" role="status">
            <i class="fas fa-envelope-open-text" aria-hidden="true"></i>
            <div>
              <strong>Invitation en attente</strong>
              <span>Envoyée le {{ formatDate(user.invited_at) }}</span>
            </div>
          </div>

          <div class="account-form-layout">
          <section class="account-form-section account-form-section--identity" aria-labelledby="identity-section-title">
            <section-heading heading-id="identity-section-title" icon="fas fa-user" title="Identité et profil" description="Informations utilisées pour identifier l’utilisateur et déterminer ses accès."></section-heading>

            <div class="account-form-grid account-form-grid--identity">
              <div class="account-field account-field--name">
                <label for="account-name">Nom <span aria-hidden="true">*</span></label>
                <div class="account-input-wrap" :class="{ 'has-error': fieldErrors.name }">
                  <i class="now-ui-icons users_circle-08" aria-hidden="true"></i>
                  <input id="account-name" v-model.trim="form.name" type="text" autocomplete="name" maxlength="255"
                         placeholder="Prénom et nom"
                         :aria-invalid="Boolean(fieldErrors.name)" :aria-describedby="fieldErrors.name ? 'account-name-error' : null"
                         aria-required="true" @input="clearFieldError('name')">
                </div>
                <span v-if="fieldErrors.name" id="account-name-error" class="account-field-error" role="alert">{{ fieldErrors.name }}</span>
              </div>

              <div class="account-field account-field--email">
                <label for="account-email">Adresse e-mail <span aria-hidden="true">*</span></label>
                <div class="account-input-wrap" :class="{ 'has-error': fieldErrors.email }">
                  <i class="now-ui-icons ui-1_email-85" aria-hidden="true"></i>
                  <input id="account-email" v-model.trim="form.email" type="email" autocomplete="email" maxlength="255"
                         placeholder="nom@entreprise.fr"
                         :aria-invalid="Boolean(fieldErrors.email)" :aria-describedby="fieldErrors.email ? 'account-email-error' : null"
                         aria-required="true" @input="clearFieldError('email')">
                </div>
                <span v-if="fieldErrors.email" id="account-email-error" class="account-field-error" role="alert">{{ fieldErrors.email }}</span>
              </div>

              <div class="account-field account-field--role">
                <label for="account-role">Profil <span aria-hidden="true">*</span></label>
                <el-select id="account-role" ref="roleSelect" v-model="form.role" class="account-select" filterable
                           placeholder="Choisir un profil" :class="{ 'has-error': fieldErrors.role }"
                           popper-class="account-select-popper"
                           :aria-invalid="Boolean(fieldErrors.role)"
                           aria-required="true" :aria-describedby="fieldErrors.role ? 'account-role-error' : null"
                           @change="clearFieldError('role')">
                  <el-option v-for="role in roles" :key="role.name" :label="role.name" :value="role.name"></el-option>
                </el-select>
                <span v-if="fieldErrors.role" id="account-role-error" class="account-field-error" role="alert">{{ fieldErrors.role }}</span>
              </div>

              <div class="account-field account-status-field account-field--status">
                <label for="account-active">Statut</label>
                <label class="account-status-toggle" :class="{ 'is-disabled': isCurrentUser }">
                  <input id="account-active" v-model="form.is_active" type="checkbox" :disabled="isCurrentUser">
                  <span class="account-status-toggle-track" aria-hidden="true"><span></span></span>
                  <span class="account-status-label">{{ form.is_active ? 'Actif' : 'Désactivé' }}</span>
                </label>
                <small v-if="isCurrentUser">Vous ne pouvez pas désactiver votre propre compte.</small>
              </div>
            </div>
          </section>

          <section class="account-form-section account-form-section--appearance" aria-labelledby="appearance-section-title">
            <section-heading heading-id="appearance-section-title" icon="fas fa-circle-notch" title="Indicateur de chargement" description="Le spinner choisi sera utilisé pendant les chargements de l’utilisateur."></section-heading>
            <div class="account-spinner-layout">
              <div class="account-field">
                <label for="account-spinner">Spinner <span aria-hidden="true">*</span></label>
                <el-select id="account-spinner" ref="spinnerSelect" v-model="form.spinner" class="account-select"
                           placeholder="Choisir un spinner" :class="{ 'has-error': fieldErrors.spinner }"
                           popper-class="account-select-popper"
                           :aria-invalid="Boolean(fieldErrors.spinner)"
                           aria-required="true" :aria-describedby="fieldErrors.spinner ? 'account-spinner-error' : null"
                           @change="clearFieldError('spinner')">
                  <el-option v-for="option in spinnerOptions" :key="option.value" :label="option.label" :value="option.value"></el-option>
                </el-select>
                <span v-if="fieldErrors.spinner" id="account-spinner-error" class="account-field-error" role="alert">{{ fieldErrors.spinner }}</span>
              </div>
              <div class="account-spinner-preview" aria-live="polite">
                <div class="account-spinner-preview-copy">
                  <small>Aperçu</small>
                  <strong>{{ selectedSpinnerLabel }}</strong>
                </div>
                <div class="account-spinner-preview-visual" aria-hidden="true">
                  <spinner :key="form.spinner" :variant="form.spinner" :preview="true" size="46px"></spinner>
                </div>
              </div>
            </div>
          </section>

          <section class="account-form-section account-form-section--access" aria-labelledby="access-section-title">
            <div class="account-form-section-header">
              <section-heading heading-id="access-section-title" icon="fas fa-key" title="Accès au compte"
                               :description="isEditMode ? 'Laisser les champs vides pour conserver le mot de passe actuel.' : 'Choisir comment l’utilisateur recevra son premier accès.'"></section-heading>
              <n-button v-if="isEditMode && canSendPasswordReset" type="default" size="sm"
                        class="account-button account-button--secondary account-section-action"
                        :disabled="isBusy" @click.native="sendPasswordReset">
                <span><i class="fas fa-unlock-alt mr-2" aria-hidden="true"></i>{{ isSendingReset ? 'Envoi…' : 'Envoyer un lien de réinitialisation' }}</span>
              </n-button>
            </div>

            <div v-if="!isEditMode" class="account-creation-options" role="radiogroup" aria-label="Mode de création du compte">
              <label class="account-creation-option" :class="{ 'is-selected': creationMode === 'invitation' }">
                <input v-model="creationMode" type="radio" name="account-creation-mode" value="invitation">
                <span class="account-creation-option-icon"><i class="fas fa-paper-plane" aria-hidden="true"></i></span>
                <span class="account-creation-option-copy"><strong>Envoyer une invitation</strong><small>L’utilisateur définit lui-même son mot de passe.</small></span>
                <span class="account-creation-option-check" aria-hidden="true"><i class="fas fa-check"></i></span>
              </label>
              <label class="account-creation-option" :class="{ 'is-selected': creationMode === 'manual' }">
                <input v-model="creationMode" type="radio" name="account-creation-mode" value="manual">
                <span class="account-creation-option-icon"><i class="fas fa-lock" aria-hidden="true"></i></span>
                <span class="account-creation-option-copy"><strong>Définir le mot de passe</strong><small>L’administrateur communique les accès à l’utilisateur.</small></span>
                <span class="account-creation-option-check" aria-hidden="true"><i class="fas fa-check"></i></span>
              </label>
            </div>

            <div v-if="showPasswordFields" class="account-form-grid account-password-grid">
              <password-field id="account-password" v-model="form.password" label="Mot de passe"
                              :required="!isEditMode" :visible.sync="showPassword" :error="fieldErrors.password"
                              @input="clearFieldError('password')"></password-field>
              <password-field id="account-password-confirmation" v-model="form.password_confirmation" label="Confirmation"
                              :required="!isEditMode" :visible.sync="showPasswordConfirmation"
                              :error="fieldErrors.password_confirmation" @input="clearFieldError('password_confirmation')"></password-field>

              <div class="account-password-meter" aria-live="polite">
                <div class="account-password-meter-heading">
                  <span>Robustesse</span>
                  <strong :class="`strength-${passwordStrength.level}`">{{ passwordStrength.label }}</strong>
                </div>
                <div class="account-password-meter-track" role="progressbar" aria-label="Robustesse du mot de passe"
                     aria-valuemin="0" aria-valuemax="5" :aria-valuenow="passwordStrength.score">
                  <span v-for="segment in 5" :key="segment"
                        :class="{ active: segment <= passwordStrength.score, [`strength-${passwordStrength.level}`]: segment <= passwordStrength.score }"></span>
                </div>
                <small>8 caractères minimum, avec majuscule, minuscule, chiffre et caractère spécial.</small>
              </div>
            </div>

            <div v-if="isEditMode && (canSendInvitation || !persistedAccountIsActive)" class="account-secondary-actions">
              <n-button v-if="canSendInvitation" type="default" size="sm" class="account-button account-button--secondary" :disabled="isBusy" @click.native="sendInvitation">
                <span><i class="fas fa-paper-plane mr-2" aria-hidden="true"></i>{{ isSendingInvitation ? 'Envoi…' : 'Renvoyer l’invitation' }}</span>
              </n-button>
              <p v-if="!persistedAccountIsActive" class="account-secondary-actions-hint mb-0">
                Réactivez et enregistrez le compte avant d’envoyer un lien d’accès.
              </p>
            </div>
          </section>

          <section v-if="isEditMode" class="account-form-section account-form-section--history" aria-labelledby="history-section-title">
            <section-heading heading-id="history-section-title" icon="fas fa-history" title="Historique" description="Dernières actions enregistrées sur ce compte."></section-heading>
            <div v-if="history.length" class="account-history-list">
              <div v-for="event in history" :key="event.id || `${event.action}-${event.date || event.created_at}`" class="account-history-row">
                <span class="account-history-dot" aria-hidden="true"></span>
                <div class="account-history-action">
                  <strong>{{ historyActionLabel(event) }}</strong>
                  <small v-if="historyChangedFields(event)">{{ historyChangedFields(event) }}</small>
                </div>
                <time :datetime="event.date || event.created_at">{{ formatDate(event.date || event.created_at) }}</time>
              </div>
            </div>
            <p v-else class="account-history-empty">Aucune action enregistrée.</p>
          </section>
          </div>

          <div v-if="generalError" class="account-form-error" role="alert">
            <i class="fas fa-exclamation-circle" aria-hidden="true"></i>{{ generalError }}
          </div>
          <div class="account-form-footer">
            <div class="account-form-footer-actions">
              <n-button type="default" class="account-button account-button--secondary" :disabled="isBusy" @click.native="backToList">Annuler</n-button>
              <n-button type="primary" class="account-button account-button--primary" native-type="submit" :disabled="isBusy">
                <span v-if="isSaving"><i class="fas fa-circle-notch fa-spin mr-2" aria-hidden="true"></i>Enregistrement…</span>
                <span v-else><i class="fas fa-check mr-2" aria-hidden="true"></i>{{ isEditMode ? 'Enregistrer les modifications' : 'Créer le compte' }}</span>
              </n-button>
            </div>
          </div>
        </form>
      </card>

    </div>
  </div>
</template>

<script>
import { Select, Option } from 'element-ui'
import Spinner from '@/components/Spinner/Circle'
import { DEFAULT_SPINNER, SPINNER_OPTIONS, getSpinnerLabel } from '@/components/Spinner/spinnerOptions'
import SectionHeading from './UserFormSectionHeading.vue'
import PasswordField from './UserPasswordField.vue'

const emptyForm = () => ({ name: '', email: '', role: '', spinner: DEFAULT_SPINNER, is_active: true, password: '', password_confirmation: '' })

export default {
  name: 'UserForm',
  components: { [Select.name]: Select, [Option.name]: Option, Spinner, SectionHeading, PasswordField },
  data() {
    return {
      form: emptyForm(), user: {}, roles: [], history: [], spinnerOptions: SPINNER_OPTIONS,
      creationMode: 'invitation', fieldErrors: {}, generalError: '', loadError: '', isLoading: false,
      isSaving: false, isSendingInvitation: false, isSendingReset: false,
      showPassword: false, showPasswordConfirmation: false
    }
  },
  computed: {
    isEditMode() { return Boolean(this.$route.params.id) },
    isCurrentUser() {
      const currentId = window.App && window.App.id
      return Boolean(this.user.is_current) || Boolean(currentId && Number(currentId) === Number(this.$route.params.id))
    },
    isInvitationPending() { return Boolean(this.user.invited_at && !this.user.invitation_accepted_at) },
    persistedAccountIsActive() {
      return this.user.is_active === true || this.user.is_active === 1 || this.user.is_active === '1'
    },
    canSendInvitation() { return this.persistedAccountIsActive && this.isInvitationPending },
    canSendPasswordReset() { return this.persistedAccountIsActive && !this.isInvitationPending },
    isBusy() { return this.isSaving || this.isSendingInvitation || this.isSendingReset },
    showPasswordFields() { return this.isEditMode || this.creationMode === 'manual' },
    selectedSpinnerLabel() { return getSpinnerLabel(this.form.spinner) },
    passwordStrength() {
      const password = this.form.password || ''
      const score = [password.length >= 8, /[A-Z]/.test(password), /[a-z]/.test(password), /[0-9]/.test(password), /[^A-Za-z0-9]/.test(password)].filter(Boolean).length
      if (score <= 2) return { score, label: 'Faible', level: 'weak' }
      if (score <= 4) return { score, label: 'Moyen', level: 'medium' }
      return { score, label: 'Fort', level: 'strong' }
    }
  },
  watch: {
    '$route.params.id'(nextId, previousId) {
      if (String(nextId || '') === String(previousId || '')) return
      this.form = emptyForm()
      this.user = {}
      this.history = []
      this.creationMode = 'invitation'
      this.fieldErrors = {}
      this.generalError = ''
      this.initialiseForm()
    }
  },
  mounted() {
    this.initialiseForm()
  },
  updated() {
    this.syncSelectAccessibility()
  },
  methods: {
    syncSelectAccessibility() {
      [
        { ref: 'roleSelect', error: this.fieldErrors.role, errorId: 'account-role-error' },
        { ref: 'spinnerSelect', error: this.fieldErrors.spinner, errorId: 'account-spinner-error' }
      ].forEach(({ ref, error, errorId }) => {
        const select = this.$refs[ref]
        const reference = select && select.$refs ? select.$refs.reference : null
        const input = reference && reference.$refs ? reference.$refs.input : null

        if (!input) return

        input.setAttribute('aria-required', 'true')
        input.setAttribute('aria-invalid', String(Boolean(error)))
        if (error) input.setAttribute('aria-describedby', errorId)
        else input.removeAttribute('aria-describedby')
      })
    },
    async initialiseForm() {
      this.isLoading = true; this.loadError = ''; this.generalError = ''
      try { await this.loadRoles(); if (this.isEditMode) await this.loadUser() }
      catch (error) { this.loadError = this.errorMessage(error, 'Une erreur est survenue pendant le chargement.') }
      finally { this.isLoading = false }
    },
    async loadRoles() {
      const response = await this.axios.get(`${process.env.VUE_APP_API_BASE_URL}role/list`)
      const payload = response.data && response.data.data ? response.data.data : response.data
      this.roles = (Array.isArray(payload) ? payload : []).map(role => ({ ...role, name: role.name || role.label, permissions: Array.isArray(role.permissions) ? role.permissions : [] }))
      if (!this.form.role && this.roles.length) {
        const defaultRole = this.roles.find(role => role.name === 'Utilisateur')
        this.form.role = defaultRole ? defaultRole.name : this.roles[0].name
      }
    },
    async loadUser(preserveForm = false) {
      const response = await this.axios.get(`${process.env.VUE_APP_API_BASE_URL}users/${this.$route.params.id}`)
      const user = response.data && response.data.data ? response.data.data : response.data
      this.user = user || {}
      if (!preserveForm) {
        const roleName = user.role && typeof user.role === 'object' ? user.role.name : user.role
        this.form = { ...emptyForm(), name: user.name || '', email: user.email || '', role: roleName || 'Utilisateur', spinner: user.spinner || DEFAULT_SPINNER, is_active: user.is_active === undefined ? true : Boolean(Number(user.is_active)) }
      }
      this.history = Array.isArray(user.history) ? user.history : []
    },
    async submitForm() {
      if (this.isBusy) return
      if (!this.validateForm()) return
      this.isSaving = true; this.generalError = ''
      try {
        const payload = { name: this.form.name, email: this.form.email, role: this.form.role, spinner: this.form.spinner, is_active: this.form.is_active }
        if (this.isEditMode) {
          if (this.form.password) Object.assign(payload, { password: this.form.password, password_confirmation: this.form.password_confirmation })
          await this.axios.patch(`${process.env.VUE_APP_API_BASE_URL}users/${this.$route.params.id}`, payload)
          if (this.isCurrentUser) await this.$store.dispatch('profile/me')
          this.notify('Le compte a été mis à jour.', 'success')
        } else {
          payload.creation_mode = this.creationMode
          if (this.creationMode === 'invitation') payload.redirect_url = this.passwordResetUrl()
          if (this.creationMode === 'manual') Object.assign(payload, { password: this.form.password, password_confirmation: this.form.password_confirmation })
          await this.axios.post(`${process.env.VUE_APP_API_BASE_URL}users`, payload)
          this.notify(this.creationMode === 'invitation' ? 'Le compte a été créé et l’invitation a été envoyée.' : 'Le compte a été créé.', 'success')
        }
        this.goToList()
      } catch (error) { this.applyApiErrors(error) }
      finally { this.isSaving = false }
    },
    validateForm() {
      const errors = {}
      if (!this.form.name) errors.name = 'Le nom est obligatoire.'
      else if (this.form.name.length > 255) errors.name = 'Le nom ne peut pas dépasser 255 caractères.'
      if (!this.form.email) errors.email = 'L’adresse e-mail est obligatoire.'
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) errors.email = 'L’adresse e-mail n’est pas valide.'
      else if (this.form.email.length > 255) errors.email = 'L’adresse e-mail ne peut pas dépasser 255 caractères.'
      if (!this.form.role) errors.role = 'Le profil est obligatoire.'
      if (!this.form.spinner) errors.spinner = 'Le spinner est obligatoire.'
      const passwordRequired = !this.isEditMode && this.creationMode === 'manual'
      const passwordIsBeingEdited = this.showPasswordFields && Boolean(this.form.password || this.form.password_confirmation)
      if (passwordRequired && !this.form.password) errors.password = 'Le mot de passe est obligatoire.'
      if (passwordIsBeingEdited && !this.form.password) errors.password = 'Saisissez le nouveau mot de passe.'
      if (this.showPasswordFields && this.form.password && this.passwordStrength.score < 5) errors.password = 'Le mot de passe ne respecte pas toutes les règles de sécurité.'
      if ((passwordRequired || passwordIsBeingEdited) && this.form.password !== this.form.password_confirmation) errors.password_confirmation = 'Les mots de passe ne correspondent pas.'
      this.fieldErrors = errors
      this.generalError = Object.keys(errors).length ? 'Certains champs doivent être corrigés.' : ''
      return Object.keys(errors).length === 0
    },
    applyApiErrors(error) {
      const responseErrors = error.response && error.response.data && error.response.data.errors
      const errors = {}
      if (responseErrors && !Array.isArray(responseErrors)) {
        Object.keys(responseErrors).forEach(field => { errors[field] = Array.isArray(responseErrors[field]) ? responseErrors[field][0] : responseErrors[field] })
      } else if (Array.isArray(responseErrors)) {
        responseErrors.forEach(item => { const pointer = item.source && item.source.pointer; const field = pointer && pointer.split('/').pop(); if (field) errors[field] = item.detail || item.title })
      }
      this.fieldErrors = errors
      this.generalError = this.errorMessage(error, 'Impossible d’enregistrer le compte. Vérifie les informations saisies.')
    },
    clearFieldError(field) { if (this.fieldErrors[field]) { const errors = { ...this.fieldErrors }; delete errors[field]; this.fieldErrors = errors } },
    async sendInvitation() {
      if (this.isBusy) return
      this.isSendingInvitation = true
      try {
        await this.axios.post(`${process.env.VUE_APP_API_BASE_URL}users/${this.$route.params.id}/invite`, { redirect_url: this.passwordResetUrl() })
        this.notify('L’invitation a été envoyée.', 'success')
        try { await this.loadUser(true) }
        catch (error) { this.notify('L’invitation est envoyée, mais l’historique n’a pas pu être actualisé.', 'warning') }
      } catch (error) { this.notify(this.errorMessage(error, 'Impossible d’envoyer l’invitation.'), 'danger') }
      finally { this.isSendingInvitation = false }
    },
    async sendPasswordReset() {
      if (this.isBusy) return
      this.isSendingReset = true
      try {
        await this.axios.post(`${process.env.VUE_APP_API_BASE_URL}users/${this.$route.params.id}/password-reset`, { redirect_url: this.passwordResetUrl() })
        this.notify('Le lien de réinitialisation a été envoyé.', 'success')
        try { await this.loadUser(true) }
        catch (error) { this.notify('Le lien est envoyé, mais l’historique n’a pas pu être actualisé.', 'warning') }
      } catch (error) { this.notify(this.errorMessage(error, 'Impossible d’envoyer le lien de réinitialisation.'), 'danger') }
      finally { this.isSendingReset = false }
    },
    passwordResetUrl() { return `${window.location.origin}/password/email` },
    errorMessage(error, fallback) { const data = error.response && error.response.data; return (data && (data.message || data.error)) || fallback },
    notify(message, type) { this.$notify({ message, type, verticalAlign: 'bottom' }) },
    historyActionLabel(event) {
      const action = typeof event === 'string' ? event : event.action
      const context = typeof event === 'object' && event.context ? event.context : {}
      const command = context.command_number ? ` ${context.command_number}` : ''
      const article = context.article_name ? `« ${context.article_name} »` : 'Article'
      const labels = {
        created: 'Compte créé',
        updated: 'Compte modifié',
        role_changed: 'Profil modifié',
        password_changed: 'Mot de passe modifié',
        activated: 'Compte réactivé',
        deactivated: 'Compte désactivé',
        deleted: 'Compte supprimé',
        invitation_sent: 'Invitation envoyée',
        invitation_accepted: 'Invitation acceptée',
        password_reset_sent: 'Lien de réinitialisation envoyé',
        command_created: `Commande${command} créée`,
        command_updated: `Commande${command} modifiée`,
        command_deleted: `Commande${command} supprimée`,
        command_item_created: `${article} ajouté à la commande${command}`,
        command_item_updated: `${article} modifié dans la commande${command}`,
        command_item_deleted: `${article} retiré de la commande${command}`
      }

      if (action === 'command_status_changed') {
        if (context.requested_state === 'urgent') return `Commande${command} marquée urgente`
        if (context.requested_state === 'not urgent') return `Urgence retirée de la commande${command}`
        return `Statut de la commande${command} : ${this.historyStatusLabel(context.previous_status, 'command')} → ${this.historyStatusLabel(context.current_status, 'command')}`
      }

      if (action === 'command_item_status_changed') {
        return `Statut de ${article} dans la commande${command} : ${this.historyStatusLabel(context.previous_status, 'item')} → ${this.historyStatusLabel(context.current_status, 'item')}`
      }

      return labels[action] || action
    },
    historyStatusLabel(status, subject) {
      const item = subject === 'item'
      return ({
        todo: 'À faire',
        urgent: 'Urgente',
        subcontracted: 'En sous-traitance',
        standby: 'En attente',
        prepared: item ? 'Préparé' : 'Préparée',
        departed: item ? 'Expédié' : 'Expédiée',
        canceled: item ? 'Annulé' : 'Annulée'
      })[status] || status || '—'
    },
    historyChangedFields(event) {
      const fields = event && event.context && Array.isArray(event.context.changed_fields)
        ? event.context.changed_fields
        : []
      if (!fields.length || !['command_updated', 'command_item_updated'].includes(event.action)) return ''
      const labels = {
        command_number: 'numéro',
        customer_command_number: 'n° client',
        command_date: 'date de commande',
        delivery_date_requested: 'date de livraison',
        situation: 'situation',
        comment: 'commentaire',
        information: 'information',
        commercial_id: 'commercial',
        customer_id: 'client',
        final_customer_id: 'client final',
        amount: 'quantité',
        metadatas: 'caractéristiques'
      }
      return `Modifications : ${fields.map(field => labels[field] || field).join(', ')}`
    },
    formatDate(value) { return value ? new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '—' },
    backToList() { if (!this.isBusy) this.goToList() },
    goToList() { this.$router.push({ name: 'User List' }) }
  }
}
</script>

<style>
.account-form-page {
  color: #2c2c2c;
  margin-left: 0;
  margin-right: 0;
  padding-bottom: 24px;
  width: 100%;
}

.account-form-column {
  margin-left: auto;
  margin-right: auto;
  max-width: none;
}

.account-form-card {
  border: 1px solid #e5e8ee;
  border-radius: 14px;
  box-shadow: 0 14px 36px rgba(35, 48, 68, .09);
  margin-bottom: 0;
  overflow: hidden;
}

.account-form-card-header {
  background: linear-gradient(135deg, #ffffff 0%, #fff8f5 100%);
  border-bottom: 1px solid #ece8e6 !important;
  border-radius: 14px 14px 0 0;
  padding: 18px 20px !important;
}

.account-form-card-body {
  background: #f6f8fb;
  border-radius: 0 0 14px 14px;
  padding: 0 !important;
}

.account-form-header {
  align-items: center;
  display: flex;
  gap: 24px;
  justify-content: space-between;
}

.account-form-header .card-title {
  color: #252a31;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  margin: 0 0 3px !important;
  padding-left: 0 !important;
}

.account-form-eyebrow {
  color: #f96332;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .09em;
  margin: 0 0 3px;
  text-transform: uppercase;
}

.account-form-subtitle {
  color: #8b92a5;
  font-size: 11px;
  line-height: 1.35;
  margin: 0;
}

.account-back-button {
  flex: 0 0 auto;
}

.account-button.btn {
  align-items: center;
  border-radius: 8px;
  box-shadow: none !important;
  display: inline-flex;
  font-size: 11px;
  font-weight: 600;
  height: 40px;
  justify-content: center;
  margin: 0 !important;
  padding: 0 14px;
  text-transform: none !important;
  transition: background-color .16s ease, border-color .16s ease, color .16s ease;
}

.account-button--primary.btn {
  background: #f96332;
  border: 1px solid #f96332;
  color: #fff;
}

.account-button--primary.btn:hover:not(:disabled),
.account-button--primary.btn:focus:not(:disabled) {
  background: #e95420;
  border-color: #e95420;
  color: #fff;
}

.account-button--secondary.btn {
  background: #fff;
  border: 1px solid #d8dde5;
  color: #526078;
}

.account-button--secondary.btn:hover:not(:disabled),
.account-button--secondary.btn:focus:not(:disabled) {
  background: #fff7f4;
  border-color: #f8b49e;
  color: #d94c1e;
}

.account-button.btn:focus-visible {
  box-shadow: 0 0 0 3px rgba(249, 99, 50, .2) !important;
  outline: 0;
}

.account-button.btn:disabled {
  cursor: not-allowed;
  opacity: .55;
}

.account-form {
  background: #f6f8fb;
  border-radius: 0 0 14px 14px;
  padding: 18px 20px 20px;
}

.account-form-section {
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(35, 48, 68, .045);
  margin-bottom: 0;
  padding: 18px;
}

.account-form-layout {
  display: grid;
  gap: 16px;
}

.account-form-section-heading {
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.account-form-section-header {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 16px;
}

.account-form-section-header .account-form-section-heading {
  margin-bottom: 0;
}

.account-section-action {
  flex: 0 0 auto;
}

.account-form-section-heading > div {
  display: block;
  min-width: 0;
}

.account-form-section-heading h5 {
  color: #252a31;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0;
}

.account-form-section-heading p {
  color: #7a8591;
  font-size: 12px;
  line-height: 1.4;
  margin: 2px 0 0;
}

.account-form-section-icon {
  align-items: center;
  background: #fff1ec;
  border-radius: 8px;
  color: #f96332;
  display: inline-flex;
  flex: 0 0 36px;
  font-size: 14px;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.account-form-grid {
  display: grid;
  gap: 16px 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.account-field {
  min-width: 0;
}

.account-field > label:not(.account-status-toggle) {
  color: #4b5565;
  display: block;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.35;
  margin-bottom: 6px;
}

.account-input-wrap {
  align-items: center;
  background: #fff;
  border: 1px solid #d8dde5;
  border-radius: 8px;
  display: flex;
  height: 42px;
  overflow: hidden;
  transition: border-color .16s ease, box-shadow .16s ease;
}

.account-input-wrap:hover {
  border-color: #c8ced8;
}

.account-input-wrap:focus-within {
  border-color: #f96332;
  box-shadow: 0 0 0 3px rgba(249, 99, 50, .14);
}

.account-input-wrap.has-error {
  border-color: #df3b38;
}

.account-input-wrap > i {
  color: #9299a5;
  flex: 0 0 40px;
  font-size: 14px;
  text-align: center;
}

.account-input-wrap input {
  background: transparent;
  border: 0;
  color: #2c2c2c;
  flex: 1;
  font-size: 12px;
  height: 40px;
  min-width: 0;
  outline: 0;
  padding: 0 12px 0 0;
}

.account-input-wrap input::placeholder {
  color: #a0a7b2;
  opacity: 1;
}

.account-password-visibility {
  align-items: center;
  background: transparent;
  border: 0;
  color: #687386;
  cursor: pointer;
  display: inline-flex;
  height: 40px;
  justify-content: center;
  width: 40px;
}

.account-password-visibility:hover {
  color: #f96332;
}

.account-field-error {
  color: #d92d20;
  display: block;
  font-size: 11px;
  margin-top: 5px;
}

.account-select {
  display: block;
  height: 42px;
  width: 100%;
}

.account-form-page .account-select.el-select .el-input,
.account-form-page .account-select.el-select .el-input .el-input__inner {
  height: 42px;
}

.account-form-page .account-select.el-select .el-input .el-input__inner {
  background: #fff !important;
  border: 1px solid #d8dde5 !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  color: #2c2c2c !important;
  font-size: 12px;
  line-height: 42px;
  margin: 0 !important;
  padding: 0 36px 0 12px;
}

.account-form-page .account-select.el-select .el-input .el-input__inner::placeholder {
  color: #a0a7b2 !important;
  opacity: 1;
}

.account-form-page .account-select.el-select:hover .el-input .el-input__inner {
  border-color: #c8ced8 !important;
}

.account-form-page .account-select.el-select .el-input .el-input__inner:focus,
.account-form-page .account-select.el-select .el-input.is-focus .el-input__inner {
  background: #fff !important;
  border-color: #f96332 !important;
  box-shadow: 0 0 0 3px rgba(249, 99, 50, .14) !important;
  color: #2c2c2c !important;
}

.account-form-page .account-select.el-select .el-input .el-input__icon,
.account-form-page .account-select.el-select .el-input .el-select__caret {
  color: #f96332 !important;
  line-height: 42px;
}

.account-form-page .account-select.el-select.has-error .el-input .el-input__inner {
  border-color: #df3b38 !important;
}

.el-select-dropdown.el-popper.account-select-popper {
  background: #fff !important;
  border: 1px solid #e1e5eb !important;
  border-radius: 8px !important;
  box-shadow: 0 12px 30px rgba(35, 48, 68, .16) !important;
  margin-top: 6px !important;
  z-index: 12050 !important;
}

.el-select-dropdown.el-popper.account-select-popper .el-select-dropdown__item {
  color: #465266;
  font-size: 12px;
  height: 34px;
  line-height: 34px;
  padding: 0 12px;
}

.el-select-dropdown.el-popper.account-select-popper .el-select-dropdown__item.hover,
.el-select-dropdown.el-popper.account-select-popper .el-select-dropdown__item:hover {
  background: #fff4f0;
}

.el-select-dropdown.el-popper.account-select-popper .el-select-dropdown__item.selected {
  color: #f96332;
  font-weight: 700;
}

.account-status-field small {
  color: #7a8591;
  display: block;
  font-size: 11px;
  margin-top: 6px;
}

.account-status-field {
  justify-self: center;
  max-width: 100%;
  text-align: center;
  width: max-content;
}

.account-status-field > label:first-child {
  text-align: center;
}

.account-status-toggle {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e0e5ec;
  border-radius: 8px;
  cursor: pointer;
  display: flex !important;
  gap: 9px;
  height: 42px;
  margin: 0 !important;
  padding: 0 12px;
  max-width: 150px;
  width: max-content;
}

.account-status-toggle:not(.is-disabled):hover {
  border-color: #c8ced8;
}

.account-status-toggle input {
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;
}

.account-status-toggle-track {
  background: #c9ced6;
  border-radius: 20px;
  display: inline-flex;
  flex: 0 0 40px;
  height: 22px;
  padding: 3px;
  transition: background .16s ease;
  width: 40px;
}

.account-status-toggle-track span {
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .2);
  height: 16px;
  transform: translateX(0);
  transition: transform .16s ease;
  width: 16px;
}

.account-status-toggle input:checked + .account-status-toggle-track {
  background: #18a66a;
}

.account-status-toggle input:checked + .account-status-toggle-track span {
  transform: translateX(18px);
}

.account-status-toggle input:focus-visible + .account-status-toggle-track {
  box-shadow: 0 0 0 3px rgba(249, 99, 50, .22);
}

.account-status-toggle.is-disabled {
  cursor: not-allowed;
  opacity: .6;
}

.account-status-label {
  color: #364152;
  font-size: 12px;
  font-weight: 700;
}

.account-spinner-layout {
  align-items: end;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1.25fr) minmax(260px, .75fr);
}

.account-spinner-preview {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e5e8ee;
  border-radius: 9px;
  display: flex;
  height: 106px;
  justify-content: space-between;
  overflow: hidden;
  padding: 8px 10px 8px 16px;
}

.account-spinner-preview-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.account-spinner-preview-copy small {
  color: #8a93a1;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.account-spinner-preview-copy strong {
  color: #3d4654;
  font-size: 12px;
  font-weight: 700;
  margin-top: 2px;
}

.account-spinner-preview-visual {
  align-items: center;
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 8px;
  display: flex;
  flex: 0 1 220px;
  height: 90px;
  justify-content: center;
  max-width: 58%;
  overflow: hidden;
  width: 220px;
}

.account-spinner-preview-visual .spinner-host--preview {
  margin: 0 !important;
  min-height: 88px !important;
  width: 100% !important;
}

.account-spinner-preview-visual .spinner--circle .path {
  stroke: #f96332;
}

.account-creation-options {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.account-form-card .account-creation-option {
  align-items: center;
  background: #fff;
  border: 1px solid #dfe3e9;
  border-radius: 9px;
  cursor: pointer;
  display: flex;
  gap: 12px;
  margin: 0;
  min-height: 72px;
  padding: 12px 14px;
  position: relative;
  transition: background-color .16s ease, border-color .16s ease, box-shadow .16s ease;
}

.account-creation-option:hover {
  background: #fffaf8;
  border-color: #f5b29b;
}

.account-creation-option.is-selected {
  background: #fff6f2;
  border-color: #f96332;
  box-shadow: 0 0 0 2px rgba(249, 99, 50, .08);
}

.account-creation-option > input {
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;
}

.account-creation-option-icon {
  align-items: center;
  background: #fff0eb;
  border-radius: 8px;
  color: #f96332;
  display: inline-flex;
  flex: 0 0 38px;
  font-size: 15px;
  height: 38px;
  justify-content: center;
  transition: background-color .16s ease, color .16s ease;
  width: 38px;
}

.account-creation-option.is-selected .account-creation-option-icon {
  background: #f96332;
  color: #fff;
}

.account-creation-option-copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.account-creation-option strong {
  color: #354052;
  font-size: 12px;
  font-weight: 700;
}

.account-creation-option small {
  color: #7a8591;
  font-size: 11px;
  line-height: 1.35;
}

.account-creation-option-check {
  align-items: center;
  border: 1px solid #ccd2db;
  border-radius: 50%;
  color: transparent;
  display: inline-flex;
  flex: 0 0 20px;
  font-size: 9px;
  height: 20px;
  justify-content: center;
  width: 20px;
}

.account-creation-option.is-selected .account-creation-option-check {
  background: #f96332;
  border-color: #f96332;
  color: #fff;
}

.account-creation-option > input:focus-visible ~ .account-creation-option-check {
  box-shadow: 0 0 0 3px rgba(249, 99, 50, .22);
}

.account-creation-options + .account-password-grid {
  margin-top: 18px;
}

.account-password-meter {
  background: #f8fafc;
  border: 1px solid #e7eaf0;
  border-radius: 8px;
  grid-column: 1 / -1;
  padding: 11px 12px;
}

.account-password-meter-heading {
  display: flex;
  font-size: 11px;
  justify-content: space-between;
  margin-bottom: 7px;
}

.account-password-meter-track {
  display: grid;
  gap: 5px;
  grid-template-columns: repeat(5, 1fr);
}

.account-password-meter-track span {
  background: #e3e7ec;
  border-radius: 4px;
  height: 5px;
}

.account-password-meter-track span.active.strength-weak { background: #e53935; }
.account-password-meter-track span.active.strength-medium { background: #f6a623; }
.account-password-meter-track span.active.strength-strong { background: #18a66a; }
.account-password-meter .strength-weak { color: #e53935; }
.account-password-meter .strength-medium { color: #c77b00; }
.account-password-meter .strength-strong { color: #168453; }

.account-password-meter small {
  color: #7a8591;
  display: block;
  font-size: 10px;
  margin-top: 6px;
}

.account-secondary-actions {
  border-top: 1px solid #edf0f4;
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 18px;
  padding-top: 16px;
}

.account-form-notice {
  align-items: center;
  background: #fff9e9;
  border: 1px solid #efd38a;
  border-radius: 9px;
  color: #805f16;
  display: flex;
  gap: 11px;
  margin: 0 0 14px;
  padding: 11px 14px;
}

.account-form-notice > i {
  font-size: 16px;
}

.account-form-notice div {
  display: flex;
  flex-direction: column;
}

.account-form-notice span {
  font-size: 11px;
}

.account-history-list {
  border: 1px solid #e8ebf0;
  border-radius: 8px;
  max-height: 360px;
  overflow-x: hidden;
  overflow-y: auto;
}

.account-history-row {
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #eff1f4;
  display: grid;
  gap: 11px;
  grid-template-columns: 9px minmax(0, 1fr) auto;
  padding: 10px 12px;
}

.account-history-row:last-child {
  border-bottom: 0;
}

.account-history-dot {
  background: #f96332;
  border-radius: 50%;
  height: 7px;
  width: 7px;
}

.account-history-row strong {
  font-size: 12px;
  font-weight: 600;
}

.account-history-action {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.account-history-action small {
  color: #7a8591;
  font-size: 10px;
  margin-top: 2px;
}

.account-history-row time {
  color: #7a8591;
  font-size: 11px;
}

.account-history-empty,
.account-secondary-actions-hint {
  color: #7a8591;
  font-size: 11px;
  margin: 0;
}

.account-form-error {
  align-items: center;
  background: #fff0ef;
  border: 1px solid #f0c3c0;
  border-radius: 8px;
  color: #c62828;
  display: flex;
  gap: 9px;
  margin: 0 0 14px;
  padding: 11px 13px;
}

.account-form-footer {
  align-items: center;
  background: rgba(255, 255, 255, .96);
  border-top: 1px solid #e4e8ee;
  bottom: 0;
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin: 18px -20px -20px;
  padding: 15px 20px;
  position: sticky;
  z-index: 20;
}

.account-form-footer-actions {
  align-items: center;
  display: flex;
  gap: 9px;
}

.account-form-state {
  align-items: center;
  background: #fff;
  display: flex;
  flex-direction: column;
  padding: 54px 20px;
  text-align: center;
}

.account-form-state > i {
  color: #d92d20;
  font-size: 28px;
  margin-bottom: 12px;
}

.account-form-state p {
  color: #7a8591;
}

.account-form--loading {
  pointer-events: none;
}

.account-skeleton {
  animation: account-pulse 1.2s ease-in-out infinite;
  background: #e7eaf0;
  border: 0;
  border-radius: 5px;
  display: block;
}

.account-skeleton-heading .account-form-section-icon {
  background: #e7eaf0;
}

.account-skeleton-heading {
  min-height: 36px;
}

.account-skeleton-heading .account-form-section-heading-copy {
  width: min(100%, 430px);
}

.account-skeleton--heading {
  height: 14px;
  width: 126px;
}

.account-skeleton--heading-long { width: 180px; }

.account-skeleton-field {
  height: 63px;
}

.account-skeleton-field--status {
  justify-self: center;
  width: 104px;
}

.account-skeleton-field--status .account-skeleton--label {
  margin-left: auto;
  margin-right: auto;
  width: 46px;
}

.account-skeleton--label {
  height: 15px;
  margin-bottom: 6px;
  width: 72px;
}

.account-skeleton--control {
  border-radius: 8px;
  height: 42px;
  width: 100%;
}

.account-skeleton-preview .account-spinner-preview-copy {
  flex: 1;
}

.account-skeleton--preview-label {
  height: 10px;
  width: 48px;
}

.account-skeleton--preview-name {
  height: 12px;
  margin-top: 7px;
  width: 112px;
}

.account-skeleton-preview .account-spinner-preview-visual {
  background: #e7eaf0;
  border: 0;
}

.account-form-card .account-creation-option--skeleton {
  cursor: default;
}

.account-creation-option--skeleton .account-creation-option-icon,
.account-creation-option--skeleton .account-creation-option-check {
  background: #e7eaf0;
  border: 0;
}

.account-skeleton--option-title {
  height: 12px;
  width: 135px;
}

.account-skeleton--option-copy {
  height: 11px;
  margin-top: 5px;
  max-width: 100%;
  width: 240px;
}

.account-skeleton--section-action {
  border-radius: 8px;
  flex: 0 0 228px;
  height: 40px;
  width: 228px;
}

.account-password-meter--skeleton .account-password-meter-track span {
  background: #e7eaf0;
}

.account-skeleton--meter-label,
.account-skeleton--meter-value {
  height: 16.5px;
  width: 70px;
}

.account-skeleton--meter-value { width: 45px; }

.account-skeleton--meter-help {
  height: 15px;
  margin-top: 6px;
  width: 310px;
}

.account-skeleton--history-empty {
  display: inline-block;
  height: 12px;
  width: 155px;
}

.account-form-footer--skeleton {
  min-height: 71px;
}

.account-skeleton--footer-button {
  border-radius: 8px;
  height: 40px;
}

.account-skeleton--footer-button-secondary { width: 86px; }
.account-skeleton--footer-button-primary { width: 150px; }

.account-form--loading .account-form-footer {
  position: static;
}

@keyframes account-pulse {
  0%, 100% { opacity: .55; }
  50% { opacity: 1; }
}

@media (max-width: 991px) {
  .account-spinner-layout { grid-template-columns: minmax(0, 1fr) minmax(230px, .7fr); }
}

@media (min-width: 1200px) {
  .account-form-layout {
    grid-template-columns: minmax(300px, 4fr) minmax(0, 8fr);
  }

  .account-form-section--identity,
  .account-form-section--history {
    grid-column: 1 / -1;
  }

  .account-form-section--appearance { grid-column: 1; }
  .account-form-section--access { grid-column: 2; }

  .account-form-grid--identity {
    gap: 14px;
    grid-template-columns: minmax(155px, 2.5fr) minmax(240px, 4fr) minmax(180px, 3fr) minmax(135px, 2fr);
  }

  .account-form-section--appearance .account-spinner-layout {
    gap: 12px;
    grid-template-columns: 1fr;
  }

  .account-form-section--appearance .account-spinner-preview {
    height: 82px;
  }

  .account-form-section--appearance .account-spinner-preview-visual {
    height: 66px;
  }

  .account-form-section--appearance .account-spinner-preview-visual .spinner-host--preview {
    min-height: 64px !important;
  }
}

@media (max-width: 767px) {
  .account-form-page { padding-bottom: 12px; }
  .account-form-card { border-radius: 14px; }
  .account-form-card-header { padding: 18px !important; }
  .account-form-header { align-items: flex-start; flex-direction: column; gap: 14px; }
  .account-form-header .card-title { font-size: 20px; }
  .account-form { padding: 4px 12px 12px; }
  .account-form-grid,
  .account-spinner-layout,
  .account-creation-options { grid-template-columns: 1fr; }
  .account-form-layout { gap: 12px; }
  .account-form-section { padding: 16px; }
  .account-form-section-heading { align-items: center; margin-bottom: 14px; }
  .account-form-section-heading > div { display: block; }
  .account-form-section-header { align-items: stretch; flex-direction: column; gap: 10px; margin-bottom: 14px; }
  .account-section-action.btn { width: 100%; }
  .account-skeleton--section-action { flex-basis: 40px; width: 100%; }
  .account-spinner-preview { height: 106px; }
  .account-status-description { display: none; }
  .account-history-row { align-items: start; grid-template-columns: 9px 1fr; }
  .account-history-row time { grid-column: 2; }
  .account-form-footer { align-items: stretch; flex-direction: column; margin: 10px -12px -12px; padding: 14px 12px; position: static; }
  .account-form-footer-actions { flex-direction: column-reverse; width: 100%; }
  .account-form-footer-actions .btn { width: 100%; }
  .account-form-footer--skeleton .account-form-footer-actions .account-skeleton { width: 100%; }
  .account-secondary-actions { align-items: stretch; flex-direction: column; }
  .account-secondary-actions .btn { width: 100%; }
}

@media (max-width: 420px) {
  .account-form-column { padding-left: 8px; padding-right: 8px; }
  .account-form-card-header { padding: 16px !important; }
  .account-form { padding: 10px; }
  .account-form-section { padding: 14px; }
  .account-form-footer { margin: 2px -10px -10px; padding: 12px 10px; }
  .account-creation-option { padding: 11px; }
}
</style>
